import axios, { AxiosError, type AxiosAdapter, type AxiosInstance, type AxiosRequestConfig, type RawAxiosRequestHeaders } from 'axios';
import { z } from 'zod';
import { ApiError, apiStatusSchema, type ApiStatus } from '../types/api';

export const bffSessionSchema = z.object({
  status: apiStatusSchema,
  stage: z.string(),
  csrfToken: z.string(),
  firstName: z.string().nullish(),
  debtAmount: z.number().nullish(),
  debtType: z.string().nullish(),
});

export type BffSession = z.infer<typeof bffSessionSchema>;

type BffClientOptions = {
  baseUrl: string;
  adapter?: AxiosAdapter;
  timeoutMs?: number;
};

type BffRequestConfig = Pick<AxiosRequestConfig, 'validateStatus' | 'params' | 'headers'>;

const CSRF_HEADER = 'X-CSRF-Token';
const SESSION_PATH = '/session';
const DEFAULT_TIMEOUT_MS = 60000;
const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;

const rotatedTokenSchema = z.object({ csrfToken: z.string() });

const isAxiosErrorWithStatus = (error: unknown, status: number): error is AxiosError => {
  return axios.isAxiosError(error) && error.response?.status === status;
};

const errorEnvelopeSchema = z.object({ status: apiStatusSchema });

const toApiStatus = (error: AxiosError, data: unknown): ApiStatus => {
  const envelope = errorEnvelopeSchema.safeParse(data);
  if (envelope.success) return envelope.data.status;
  return { code: error.response?.status ?? 0, message: error.message };
};

const toApiError = (error: unknown): Error => {
  if (error instanceof ApiError) return error;
  if (!axios.isAxiosError(error) || !error.response) {
    return error instanceof Error ? error : new Error(String(error));
  }
  const status = toApiStatus(error, error.response.data);
  return new ApiError(status.message, status);
};

const extractRotatedToken = (body: unknown): string | null => {
  const parsed = rotatedTokenSchema.safeParse(body);
  return parsed.success ? parsed.data.csrfToken : null;
};

export class BffClient {
  private readonly http: AxiosInstance;
  private token: string | null = null;
  private sessionInFlight: Promise<BffSession> | null = null;

  constructor(options: BffClientOptions) {
    const { baseUrl, adapter, timeoutMs = DEFAULT_TIMEOUT_MS } = options;
    this.http = axios.create({
      baseURL: baseUrl,
      timeout: timeoutMs,
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' },
      ...(adapter ? { adapter } : {}),
    });
  }

  get csrfToken(): string | null {
    return this.token;
  }

  async ensureSession(): Promise<BffSession> {
    if (this.sessionInFlight) return this.sessionInFlight;
    this.sessionInFlight = this.recoverOrMintSession().finally(() => {
      this.sessionInFlight = null;
    });
    return this.sessionInFlight;
  }

  async resetSession(): Promise<BffSession> {
    return this.mintSession();
  }

  async get<T>(path: string, config: BffRequestConfig = {}): Promise<T> {
    return this.send<T>({ ...config, method: 'get', url: path });
  }

  async post<T>(path: string, body: unknown, config: BffRequestConfig = {}): Promise<T> {
    return this.send<T>({ ...config, method: 'post', url: path, data: body });
  }

  private async send<T>(config: AxiosRequestConfig): Promise<T> {
    if (this.token === null) await this.ensureSession();
    try {
      return await this.attempt<T>(config);
    } catch (error) {
      if (isAxiosErrorWithStatus(error, HTTP_FORBIDDEN)) return this.retryWithFreshToken<T>(config);
      if (isAxiosErrorWithStatus(error, HTTP_UNAUTHORIZED)) await this.remintSilently();
      throw toApiError(error);
    }
  }

  private async retryWithFreshToken<T>(config: AxiosRequestConfig): Promise<T> {
    await this.refreshSession();
    try {
      return await this.attempt<T>(config);
    } catch (error) {
      throw toApiError(error);
    }
  }

  private async attempt<T>(config: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.http.request<T>({ ...config, headers: this.withCsrf(config) });
      this.adoptRotatedToken(response.data);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) this.adoptRotatedToken(error.response?.data);
      throw error;
    }
  }

  private withCsrf(config: AxiosRequestConfig): RawAxiosRequestHeaders {
    const isWrite = (config.method ?? 'get').toLowerCase() !== 'get';
    const base: RawAxiosRequestHeaders = { ...(config.headers ?? {}) };
    return isWrite && this.token ? { ...base, [CSRF_HEADER]: this.token } : base;
  }

  private adoptRotatedToken(body: unknown): void {
    const rotated = extractRotatedToken(body);
    if (rotated) this.token = rotated;
  }

  private async recoverOrMintSession(): Promise<BffSession> {
    try {
      return await this.refreshSession();
    } catch (error) {
      if (!isAxiosErrorWithStatus(error, HTTP_UNAUTHORIZED)) throw toApiError(error);
      return this.mintSession();
    }
  }

  private async refreshSession(): Promise<BffSession> {
    const response = await this.http.get(SESSION_PATH);
    return this.adoptSession(response.data);
  }

  private async mintSession(): Promise<BffSession> {
    const response = await this.http.post(SESSION_PATH, {});
    return this.adoptSession(response.data);
  }

  private async remintSilently(): Promise<void> {
    try {
      await this.mintSession();
    } catch {
      this.token = null;
    }
  }

  private adoptSession(body: unknown): BffSession {
    const session: BffSession = bffSessionSchema.parse(body);
    this.token = session.csrfToken;
    return session;
  }
}
